import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useAuth } from "../../lib/auth";

interface Reserva {
  id: number; nombreCliente: string; horaReserva: string;
  duracionMinutos: number; numPersonas: number; estado: string; telefono?: string;
}
interface Recurso { id: number; nombre: string; numMesa?: string; reservas: Reserva[]; }
interface Zona {
  id: number; nombre: string; bloqueTiempoMinutos: number;
  horaInicio: string; horaFin: string;
  recursos: Recurso[]; reservasSinRecurso: Reserva[];
}
interface Bloqueo {
  id: number; titulo: string; tipo: string;
  horaInicio?: string; horaFin?: string;
  zonaIds?: number[]; mesaNums?: (number | string)[]; afectaTodo: boolean;
}
interface OcupacionData { fecha: string; zonas: Zona[]; bloqueos: Bloqueo[]; }

function timeToMin(t: string): number {
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
}

function minToTime(min: number): string {
  const h = Math.floor(min / 60); const m = min % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtFecha(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long' });
}

const ESTADO_COLOR: Record<string, string> = {
  pendiente:  'bg-amber-400 text-amber-900',
  confirmada: 'bg-emerald-500 text-white',
  atendida:   'bg-blue-500 text-white',
  en_mesa:    'bg-indigo-500 text-white',
};

export default function OcupacionPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [sucursales, setSucursales] = useState<any[]>([]);
  const [sucId, setSucId] = useState<number | null>(null);
  const [data, setData] = useState<OcupacionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState<{ r: Reserva; x: number; y: number } | null>(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch('/api/v1/sucursales', { headers }).then(r => r.json())
      .then((d: any[]) => {
        if (Array.isArray(d) && d.length > 0) {
          setSucursales(d);
          setSucId(d[0].id);
        }
      }).catch(() => {});
  }, []);

  const cargar = useCallback(() => {
    if (!sucId || !fecha) return;
    setLoading(true);
    fetch(`/api/v1/reservas-admin/ocupacion?fecha=${fecha}&sucursalId=${sucId}`, { headers })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [sucId, fecha, token]);

  useEffect(() => { cargar(); }, [cargar]);

  // Generar slots de tiempo del día (cada 30 min)
  const HORA_INI = 9 * 60;  // 09:00
  const HORA_FIN = 23 * 60; // 23:00
  const SLOT = 30;
  const slots: string[] = [];
  for (let m = HORA_INI; m < HORA_FIN; m += SLOT) slots.push(minToTime(m));

  function getReserva(reservas: Reserva[], slotMin: number): Reserva | null {
    return reservas.find(r => {
      const ini = timeToMin(r.horaReserva);
      const fin = ini + r.duracionMinutos;
      return slotMin >= ini && slotMin < fin;
    }) ?? null;
  }

  function isBloqueo(bloqueos: Bloqueo[], zonaId: number, slotMin: number, numMesa?: string): Bloqueo | null {
    return bloqueos.find(b => {
      const tieneZonas = b.zonaIds && b.zonaIds.length > 0;
      const tieneMesas = b.mesaNums && b.mesaNums.length > 0;

      // Determinar si este bloqueo afecta a ESTA mesa/pista específica
      let afecta = false;
      if (b.afectaTodo) {
        afecta = true;
      } else if (tieneMesas) {
        // Bloqueo por mesas específicas → solo afecta si el num de esta mesa está en la lista
        afecta = !!numMesa && b.mesaNums!.some(m => String(m) === String(numMesa));
      } else if (tieneZonas) {
        // Bloqueo por zonas → afecta si la zona está en la lista
        afecta = b.zonaIds!.some(z => String(z) === String(zonaId));
      }
      // zona_ids NULL + mesa_nums NULL + afecta_todo FALSE = mal configurado, no bloquea
      if (!afecta) return false;

      // Sin restricción horaria = todo el día
      if (!b.horaInicio) return true;

      const ini = timeToMin(b.horaInicio);
      const finRaw = b.horaFin ? timeToMin(b.horaFin) : 23 * 60 + 59;

      // Cruce de medianoche
      if (finRaw < ini) return slotMin >= ini || slotMin < finRaw;
      return slotMin >= ini && slotMin < finRaw;
    }) ?? null;
  }

  const totalReservas = data?.zonas.reduce((a, z) =>
    a + z.recursos.reduce((b, r) => b + r.reservas.length, 0) + z.reservasSinRecurso.length
  , 0) ?? 0;

  return (
    <div className="min-h-screen bg-ink-50">
      {/* Header */}
      <div className="bg-white border-b border-ink-200 px-6 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/tienda')} className="text-ink-400 hover:text-ink-700">
              <ArrowLeft className="size-5" />
            </button>
            <div>
              <h1 className="font-bold text-ink-800 text-xl">Ocupación</h1>
              <p className="text-xs text-ink-500 capitalize">{fmtFecha(fecha)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Selector sucursal */}
            {sucursales.length > 1 && (
              <div className="flex gap-2">
                {sucursales.map(s => (
                  <button key={s.id} onClick={() => setSucId(s.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${sucId === s.id ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-ink-700 border-ink-200 hover:border-brand-400'}`}>
                    {s.nombre}
                  </button>
                ))}
              </div>
            )}

            {/* Navegación fecha */}
            <div className="flex items-center gap-1 bg-ink-100 rounded-lg p-1">
              <button onClick={() => setFecha(d => addDays(d, -1))}
                className="p-1.5 rounded hover:bg-white transition-colors">
                <ChevronLeft className="size-4 text-ink-600" />
              </button>
              <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
                className="bg-transparent text-sm font-medium text-ink-700 focus:outline-none cursor-pointer px-1" />
              <button onClick={() => setFecha(d => addDays(d, 1))}
                className="p-1.5 rounded hover:bg-white transition-colors">
                <ChevronRight className="size-4 text-ink-600" />
              </button>
            </div>

            <button onClick={() => setFecha(new Date().toISOString().slice(0, 10))}
              className="text-xs px-3 py-1.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-lg hover:bg-brand-100">
              Hoy
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {loading && (
          <div className="flex items-center justify-center h-48 text-ink-400">
            Cargando ocupación…
          </div>
        )}

        {!loading && data && (
          <>
            {/* Resumen rápido */}
            <div className="flex gap-3 mb-5 flex-wrap">
              <div className="card px-4 py-3 flex items-center gap-3">
                <div className="text-2xl font-bold text-ink-800">{totalReservas}</div>
                <div className="text-xs text-ink-500">reservas<br/>del día</div>
              </div>
              {data.zonas.map(z => {
                const total = z.recursos.reduce((a, r) => a + r.reservas.length, 0) + z.reservasSinRecurso.length;
                const pct = z.recursos.length > 0 ? Math.round((z.recursos.filter(r => r.reservas.length > 0).length / z.recursos.length) * 100) : 0;
                return (
                  <div key={z.id} className="card px-4 py-3 flex items-center gap-3">
                    <div className="text-2xl font-bold text-brand-700">{pct}%</div>
                    <div className="text-xs text-ink-500">{z.nombre}<br/>{total} reservas</div>
                  </div>
                );
              })}
            </div>

            {data.zonas.length === 0 && (
              <div className="card p-8 text-center text-ink-400">
                <p className="text-base mb-1">Sin zonas con selección de pista habilitada</p>
                <p className="text-sm">Activá "Permite elegir mesa" en <strong>Mi Sucursal → Zonas</strong></p>
              </div>
            )}

            {data.zonas.map(zona => (
              <div key={zona.id} className="mb-6">
                <h2 className="font-bold text-ink-700 mb-2 text-sm uppercase tracking-wide">{zona.nombre}</h2>
                <div className="card overflow-auto">
                  <table className="text-xs border-collapse" style={{ minWidth: `${120 + slots.length * 48}px` }}>
                    <thead>
                      <tr className="bg-ink-50">
                        <th className="sticky left-0 z-10 bg-ink-50 w-28 min-w-[7rem] px-3 py-2 text-left font-semibold text-ink-500 border-r border-ink-200">
                          Pista / Mesa
                        </th>
                        {slots.map(s => (
                          <th key={s} className="w-12 px-0.5 py-2 text-center font-normal text-ink-400 border-r border-ink-100">
                            {s}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {zona.recursos.length === 0 && (
                        <tr>
                          <td colSpan={slots.length + 1} className="px-3 py-6 text-center text-ink-400">
                            Sin pistas/mesas configuradas para esta zona
                          </td>
                        </tr>
                      )}
                      {zona.recursos.map((recurso, ri) => {
                        // Track which slots are already "covered" by a multi-slot block
                        const rendered = new Set<number>();
                        return (
                          <tr key={recurso.id} className={ri % 2 === 0 ? 'bg-white' : 'bg-ink-50/50'}>
                            <td className="sticky left-0 z-10 bg-inherit px-3 py-1.5 font-medium text-ink-700 border-r border-ink-200 whitespace-nowrap">
                              {recurso.nombre}
                            </td>
                            {slots.map((s, si) => {
                              const slotMin = timeToMin(s);
                              if (rendered.has(slotMin)) return null;

                              const blq = isBloqueo(data.bloqueos, zona.id, slotMin, recurso.numMesa);
                              const res = getReserva(recurso.reservas, slotMin);

                              if (blq) {
                                // Calculate colspan
                                let span = 1;
                                if (blq.horaInicio) {
                                  const fin = timeToMin(blq.horaFin ?? '23:59');
                                  span = Math.max(1, Math.ceil((fin - slotMin) / SLOT));
                                  for (let k = 1; k < span; k++) rendered.add(slotMin + k * SLOT);
                                }
                                return (
                                  <td key={s} colSpan={span}
                                    className="bg-rose-100 border border-rose-200 px-0.5 py-1 text-center align-middle"
                                    title={blq.titulo}>
                                    <span className="text-rose-600 font-medium truncate block text-[10px]">🚫 {blq.titulo}</span>
                                  </td>
                                );
                              }

                              if (res) {
                                const durSlots = Math.ceil(res.duracionMinutos / SLOT);
                                // Simple span: only first slot of reservation gets the full block
                                const isFirst = timeToMin(res.horaReserva) === slotMin;
                                if (!isFirst) {
                                  rendered.add(slotMin);
                                  return (
                                    <td key={s} className="border border-ink-100 bg-brand-100 p-0" />
                                  );
                                }
                                const colSpan = Math.min(durSlots, slots.length - si);
                                for (let k = 1; k < colSpan; k++) rendered.add(slotMin + k * SLOT);
                                const colorCls = ESTADO_COLOR[res.estado] ?? 'bg-brand-500 text-white';
                                return (
                                  <td key={s} colSpan={colSpan}
                                    className={`border border-ink-100 px-1 py-0.5 cursor-pointer transition-opacity hover:opacity-80 ${colorCls}`}
                                    onMouseEnter={e => setTooltip({ r: res, x: e.clientX, y: e.clientY })}
                                    onMouseLeave={() => setTooltip(null)}
                                    onClick={() => navigate(`/reservas/${res.id}`)}>
                                    <div className="truncate font-medium text-[10px]">{res.nombreCliente}</div>
                                    <div className="text-[9px] opacity-80">{res.horaReserva.slice(0,5)} · {res.numPersonas}p</div>
                                  </td>
                                );
                              }

                              return (
                                <td key={s} className="border border-ink-100 bg-white hover:bg-brand-50 transition-colors cursor-pointer"
                                  title={`${recurso.nombre} · ${s}`}
                                  onClick={() => navigate(`/reservas?zona=${zona.id}&recurso=${recurso.id}&hora=${s}&fecha=${fecha}`)} />
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Leyenda */}
                <div className="flex gap-3 mt-2 text-[10px] text-ink-500 flex-wrap">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-ink-200 inline-block" /> Libre</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400 inline-block" /> Pendiente</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> Confirmada</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block" /> Atendida</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-100 border border-rose-200 inline-block" /> Bloqueado</span>
                </div>
              </div>
            ))}
          </>
        )}

        {!loading && !data && (
          <div className="card p-8 text-center text-ink-400">No se pudo cargar la información de ocupación</div>
        )}
      </div>

      {/* Tooltip flotante */}
      {tooltip && (
        <div className="fixed z-50 pointer-events-none bg-ink-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl"
          style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}>
          <div className="font-semibold">{tooltip.r.nombreCliente}</div>
          <div className="opacity-80">{tooltip.r.horaReserva.slice(0,5)} · {tooltip.r.duracionMinutos}min · {tooltip.r.numPersonas}p</div>
          {tooltip.r.telefono && <div className="opacity-70">{tooltip.r.telefono}</div>}
          <div className="opacity-60 capitalize">{tooltip.r.estado}</div>
        </div>
      )}
    </div>
  );
}
